import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { StatCard, LoadingSpinner, StatusBadge } from '../../components/UI';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from 'recharts';

const COLORS = ['#3b82f6','#10b981','#ef4444','#f59e0b','#8b5cf6','#06b6d4','#f97316'];

export default function AdminReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ date_from:'', date_to:'', noc_type_id:'', dept_id:'' });
  const [nocTypes, setNocTypes] = useState([]);
  const [depts, setDepts] = useState([]);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([,v])=>v)));
    api.get(`/reports?${params}`).then(r => setData(r)).finally(() => setLoading(false));
  };

  useEffect(() => {
    Promise.all([api.get('/noc-types/public'), api.get('/masters/departments'), load()])
      .then(([n, d]) => { setNocTypes(n.noc_types||[]); setDepts(d.departments||[]); });
  }, []);

  const statusData = data?.status_wise?.map(s => ({ name: s.status.replace(/_/g,' '), value: parseInt(s.count) })) || [];
  const nocData = data?.noc_wise?.map(n => ({ name: n.noc_name?.slice(0,12), count: parseInt(n.count) })) || [];
  const deptData = data?.dept_wise?.map(d => ({ name: d.dept_name?.slice(0,15)||'N/A', count: parseInt(d.count) })) || [];
  const dailyData = data?.daily?.map(d => ({ date: d.date?.slice(5), count: parseInt(d.count) })).reverse() || [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">📊 Reports & Analytics</h1>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <input type="date" className="form-input" value={filters.date_from} onChange={e=>setFilters(f=>({...f,date_from:e.target.value}))} />
          <input type="date" className="form-input" value={filters.date_to} onChange={e=>setFilters(f=>({...f,date_to:e.target.value}))} />
          <select className="form-select" value={filters.noc_type_id} onChange={e=>setFilters(f=>({...f,noc_type_id:e.target.value}))}>
            <option value="">All NOC Types</option>
            {nocTypes.map(n=><option key={n.id} value={n.id}>{n.noc_name}</option>)}
          </select>
          <select className="form-select" value={filters.dept_id} onChange={e=>setFilters(f=>({...f,dept_id:e.target.value}))}>
            <option value="">All Departments</option>
            {depts.map(d=><option key={d.id} value={d.id}>{d.dept_name}</option>)}
          </select>
          <button onClick={load} className="btn-primary">Apply Filter</button>
        </div>
      </div>

      {loading ? <LoadingSpinner size="lg" className="py-20" /> : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <h3 className="font-semibold text-gray-800 mb-4">Status Distribution</h3>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({name,value})=>`${value}`}>
                    {statusData.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]} />)}
                  </Pie>
                  <Tooltip /><Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="card">
              <h3 className="font-semibold text-gray-800 mb-4">Applications by NOC Type</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={nocData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{fontSize:10}} />
                  <YAxis tick={{fontSize:11}} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[4,4,0,0]}>
                    {nocData.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card">
              <h3 className="font-semibold text-gray-800 mb-4">Department Wise</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={deptData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{fontSize:11}} />
                  <YAxis dataKey="name" type="category" tick={{fontSize:10}} width={100} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#6d28d9" radius={[0,4,4,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card">
              <h3 className="font-semibold text-gray-800 mb-4">Daily Trend (Last 30 Days)</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{fontSize:9}} />
                  <YAxis tick={{fontSize:11}} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Summary Table */}
          <div className="card overflow-hidden p-0">
            <div className="px-6 py-4 border-b"><h3 className="font-semibold text-gray-800">Status Summary</h3></div>
            <table className="data-table">
              <thead><tr><th>Status</th><th>Count</th><th>Percentage</th></tr></thead>
              <tbody>
                {statusData.map((s,i) => {
                  const total = statusData.reduce((a,b)=>a+b.value,0);
                  return (
                    <tr key={i}>
                      <td className="capitalize">{s.name}</td>
                      <td className="font-semibold">{s.value}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-100 rounded-full h-2">
                            <div className="h-2 rounded-full" style={{ width:`${total?((s.value/total)*100).toFixed(0):0}%`, background: COLORS[i%COLORS.length] }} />
                          </div>
                          <span className="text-xs text-gray-500 w-10">{total?((s.value/total)*100).toFixed(1):0}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
